import { useState, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';

interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description: string;
  impact?: string;
  confirmText?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  impact,
  confirmText,
  confirmLabel,
  cancelLabel,
  danger = false,
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  const resolvedConfirmLabel = confirmLabel ?? t('common.confirm');
  const resolvedCancelLabel = cancelLabel ?? t('common.cancel');
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    if (open) setInputValue('');
  }, [open]);

  const needsTextConfirm = !!confirmText;
  const canConfirm = !needsTextConfirm || inputValue === confirmText;

  const handleConfirm = () => {
    if (!canConfirm) return;
    onConfirm();
    onClose();
  };

  return (
    <AlertDialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>

        {impact && (
          <div className="rounded-md bg-status-warning-bg p-3 text-sm text-status-warning dark:bg-status-warning-bg/50 dark:text-status-warning">
            {impact}
          </div>
        )}

        {needsTextConfirm && (
          <div className="space-y-2">
            <label className="text-sm font-medium">
              <Trans
                i18nKey="confirmDialog.typeToConfirm"
                values={{ text: confirmText }}
                components={{ bold: <span className="font-mono font-bold" /> }}
              />
            </label>
            <Input
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              autoFocus
            />
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel onClick={onClose}>
            {resolvedCancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={!canConfirm}
            className={
              danger
                ? 'border-[var(--action)] bg-[var(--action)] text-[var(--on-action)] hover:border-[var(--action-hover)] hover:bg-[var(--action-hover)]'
                : ''
            }
          >
            {resolvedConfirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
